/**
 * RangeBars — a tiny dependency-free SVG chart of labelled low–high ranges.
 * Used for per-course salary progression and the starting-salary comparison on
 * the courses hub. Theme-coloured (saffron on khadi), prerender-safe, and
 * accessible (role=img + aria-label). No charting library, inherits page font.
 */
export interface RangeRow {
  label: string;
  low: number;
  high: number;
}

export function RangeBars({
  rows,
  caption,
  ariaLabel,
  unit = 'L',
}: {
  rows: RangeRow[];
  caption?: string;
  ariaLabel?: string;
  unit?: string;
}) {
  if (!rows || rows.length === 0) return null;
  const maxHigh = Math.max(...rows.map((r) => r.high)) || 1;
  const VW = 640;
  const PAD = 8;
  const ROW = 46;
  const TOP = 12;
  const fullW = VW - PAD * 2;
  const x = (v: number) => PAD + (v / maxHigh) * fullW;
  const height = TOP + rows.length * ROW + 26;
  return (
    <figure className="rounded-card border border-ink-200/80 bg-cream p-5 sm:p-6 shadow-card">
      <svg
        viewBox={`0 0 ${VW} ${height}`}
        width="100%"
        role="img"
        aria-label={ariaLabel || `Range chart: ${rows.map((r) => `${r.label} ${r.low} to ${r.high} ${unit}`).join('; ')}`}
        style={{ display: 'block', fontFamily: 'inherit' }}
      >
        {rows.map((r, i) => {
          const y = TOP + i * ROW;
          const bw = Math.max(5, x(r.high) - x(r.low));
          return (
            <g key={r.label}>
              <text x={PAD} y={y + 12} fontSize="12.5" className="fill-ink-900">{r.label}</text>
              <text x={VW - PAD} y={y + 12} fontSize="12.5" fontWeight="600" className="fill-accent-600" textAnchor="end">₹{r.low}–{r.high} {unit}</text>
              <rect x={PAD} y={y + 20} width={fullW} height="10" rx="5" className="fill-ink-100" />
              <rect x={x(r.low)} y={y + 20} width={bw} height="10" rx="5" className="fill-accent-500" />
            </g>
          );
        })}
        <line x1={PAD} y1={height - 16} x2={VW - PAD} y2={height - 16} className="stroke-ink-200" strokeWidth="1" />
        <text x={PAD} y={height - 3} fontSize="11" className="fill-ink-500">₹0</text>
        <text x={VW - PAD} y={height - 3} fontSize="11" className="fill-ink-500" textAnchor="end">₹{maxHigh} {unit}</text>
      </svg>
      {caption && <figcaption className="text-[11px] text-ink-500 mt-2 leading-snug">{caption}</figcaption>}
    </figure>
  );
}
